import React, { useState } from 'react';
import { getProfileVideos, getAIInsights } from '../services/api';
import { cacheAnalysisResult, CACHE_TYPES } from '../services/cache';
import { hasReachedLimit, incrementSearchCount } from '../services/searchLimit';
import TikTokEmbed from './TikTokEmbed';
import SearchLimitBanner from './SearchLimitBanner';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Card,
  Grid,
  Avatar,
  Paper,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ShareIcon from '@mui/icons-material/Share';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const calculateLikeRate = (video) => {
  if (!video?.stats) return 0;
  const likeCount = video.stats.diggCount || 0;
  const playCount = video.stats.playCount || 0;
  return playCount > 0 ? (likeCount / playCount) * 100 : 0;
};

const calculateCTR = (video) => {
  if (!video?.stats) return 0;
  const clickCount = video.stats.shareCount || 0;
  const playCount = video.stats.playCount || 0;
  return playCount > 0 ? (clickCount / playCount) * 100 : 0;
};

const LikeRateAnalysis = () => {
  const [username, setUsername] = useState('');
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [insights, setInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!username) {
      setError('Please enter a username');
      return;
    }

    if (hasReachedLimit()) {
      setError("You've reached the free search limit. Join the waitlist for unlimited access!");
      return;
    }

    // Clear previous data when starting a new search
    setVideos([]);
    setError('');
    setInsights(null);
    
    setLoading(true);
    try {
      const data = await getProfileVideos(username, true);
      
      console.log('API Response:', data);
      console.log('First video stats:', data?.videos?.[0]?.stats);
      
      if (!data || !data.videos) {
        throw new Error('Invalid response format from API');
      }
      
      const analysisData = {
        videos: data.videos,
        username,
        timestamp: new Date().toISOString()
      };
      
      cacheAnalysisResult(CACHE_TYPES.LIKE_RATE, '', analysisData);
      setVideos(data.videos);
      incrementSearchCount();

      // Get AI insights for like rate
      setLoadingInsights(true);
      try {
        const insightsData = await getAIInsights(data.videos, 'like_rate');
        if (insightsData?.insights) {
          setInsights(insightsData.insights);
        }
      } catch (insightError) {
        console.error('Error getting insights:', insightError);
        // Don't fail the whole component if insights fail
      } finally {
        setLoadingInsights(false);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to fetch profile videos');
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  const overallStats = videos.length > 0 ? {
    totalLikes: videos.reduce((sum, video) => sum + (video.stats?.diggCount || 0), 0),
    totalViews: videos.reduce((sum, video) => sum + (video.stats?.playCount || 0), 0),
    totalClicks: videos.reduce((sum, video) => sum + (video.stats?.shareCount || 0), 0),
  } : null;

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2, 
          mb: 4,
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
          borderRadius: 2,
          p: 3
        }}>
          <Avatar sx={{ 
            width: 56, 
            height: 56,
            background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
          }}>
            <FavoriteIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              Like Rate Analysis
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Track engagement and analyze like-to-view ratios across your content
            </Typography>
          </Box>
        </Box>

        <SearchLimitBanner />

        {/* Search Form */}
        <Card sx={{ 
          p: 3, 
          mb: 4,
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(236, 72, 153, 0.05) 100%)',
          borderRadius: 3,
        }}>
          <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            width: '100%'
          }}>
              <Box sx={{ position: 'relative' }}>
              <TextField
                fullWidth
                label="TikTok Username"
                placeholder="Enter username without @"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    bgcolor: 'white',
                    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
                    '&:hover': {
                      boxShadow: '0px 4px 25px rgba(0, 0, 0, 0.1)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0px 4px 25px rgba(0, 0, 0, 0.1)',
                    }
                  }
                }}
              />
              <Tooltip
                title="Enter a TikTok username to analyze their content's engagement metrics"
                placement="top"
                arrow
              >
                <HelpOutlineIcon 
                  sx={{ 
                    position: 'absolute',
                    right: { xs: 8, sm: 12 },
                    top: '50%',
                    transform: 'translateY(-50%)',
                    opacity: 0.6,
                    cursor: 'help'
                  }} 
                />
              </Tooltip>
            </Box>
              <Button
                variant="contained"
                disabled={loading || hasReachedLimit()}
                fullWidth
                onClick={handleSubmit}
                sx={{
                  height: 48,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #7C3AED 0%, #DB2777 100%)',
                  },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  width: '100%',
                  minHeight: 48,
                  cursor: 'pointer'
                }}
              >
              <FavoriteIcon />
              <span>{loading ? 'Analyzing...' : 'Analyze Profile'}</span>
            </Button>
            </Box>
        </Card>

        {error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && !error && videos.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              Enter a TikTok username above to analyze their like rates
            </Typography>
          </Box>
        )}

        {overallStats && (
          <>
            {/* AI Insights */}
            <Paper sx={{ 
              p: 3, 
              mb: 4,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
              color: 'white',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <SmartToyIcon sx={{ fontSize: 24, color: 'white' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                  AI Content Analysis
                </Typography>
              </Box>
              {loadingInsights ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress sx={{ color: 'white' }} />
                </Box>
              ) : insights ? (
                <Typography variant="body1" sx={{ whiteSpace: 'pre-line', color: 'rgba(255, 255, 255, 0.9)' }}>
                  {insights}
                </Typography>
              ) : (
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  No AI insights available
                </Typography>
              )}
            </Paper>
            {/* Overall Statistics */}
            <Paper sx={{ 
              p: 3, 
              mb: 4,
              borderRadius: 3,
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(236, 72, 153, 0.05) 100%)',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <FavoriteIcon sx={{ 
                  fontSize: 24,
                  background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }} />
                <Typography variant="h6" sx={{ 
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  Overall Performance
                </Typography>
              </Box>
              <Grid container spacing={{ xs: 2, sm: 3 }}>
                <Grid item xs={12} md={4}>
                  <Card sx={{ 
                    p: 3, 
                    height: '100%',
                    background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      height: '100%'
                    }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: { xs: 1, sm: 2 },
                        mb: 1
                      }}>
                        <FavoriteIcon sx={{ 
                          fontSize: { xs: 18, sm: 20 },
                          color: 'white'
                        }} />
                        <Typography variant="h6" sx={{ color: 'white', fontSize: { xs: '1rem', sm: '1.1rem' }, lineHeight: 1.2 }}>
                          Like Rate
                        </Typography>
                      </Box>
                      <Typography variant="h4" sx={{ color: 'white', mb: 2 }}>
                        {((overallStats.totalLikes / overallStats.totalViews) * 100).toFixed(2)}%
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        {overallStats.totalLikes.toLocaleString()} likes from {overallStats.totalViews.toLocaleString()} views
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card sx={{ 
                    p: 3, 
                    height: '100%',
                    background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      height: '100%'
                    }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: { xs: 1, sm: 2 },
                        mb: 1
                      }}>
                        <ShareIcon sx={{ 
                          fontSize: { xs: 18, sm: 20 },
                          color: 'white'
                        }} />
                        <Typography variant="h6" sx={{ color: 'white', fontSize: { xs: '1rem', sm: '1.1rem' }, lineHeight: 1.2 }}>
                          Click-Through Rate
                        </Typography>
                      </Box>
                      <Typography variant="h4" sx={{ color: 'white', mb: 2 }}>
                        {((overallStats.totalClicks / overallStats.totalViews) * 100).toFixed(2)}%
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        {overallStats.totalClicks.toLocaleString()} shares
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card sx={{ 
                    p: 3, 
                    height: '100%',
                    background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      height: '100%'
                    }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: { xs: 1, sm: 2 },
                        mb: 1
                      }}>
                        <VisibilityIcon sx={{ 
                          fontSize: { xs: 18, sm: 20 },
                          color: 'white'
                        }} />
                        <Typography variant="h6" sx={{ color: 'white', fontSize: { xs: '1rem', sm: '1.1rem' }, lineHeight: 1.2 }}>
                          Total Reach
                        </Typography>
                      </Box>
                      <Typography variant="h4" sx={{ color: 'white', mb: 2 }}>
                        {videos.length}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Videos analyzed
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </Paper>

            {/* Recent Videos Analysis */}
            <Paper sx={{ 
              p: 3,
              borderRadius: 3,
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(236, 72, 153, 0.05) 100%)',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <FavoriteIcon sx={{ 
                  fontSize: 24,
                  background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }} />
                <Typography variant="h6" sx={{ 
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  Recent Videos Performance
                </Typography>
              </Box>
              <Grid container spacing={3}>
                {videos.map((video) => (
                  <Grid item xs={12} sm={6} lg={4} xl={4} key={video.id}>
                    <Card sx={{ 
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      minWidth: 0,
                      width: '100%'
                    }}>
                      <Box sx={{ 
                        p: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3,
                        flex: 1
                      }}>
                        <Box sx={{ width: '100%', overflow: 'hidden' }}>
                          <TikTokEmbed url={video.videoUrl} />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {video.description}
                        </Typography>

                        {/* Metrics with new flexible layout */}
                        <Box sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 2,
                          justifyContent: 'space-between',
                          mb: 3
                        }}>
                          {/* Likes */}
                          <Box sx={{ 
                            flex: '1 1 45%',
                            display: 'flex',
                            flexDirection: 'column',
                            p: 2,
                            borderRadius: 2,
                            bgcolor: 'rgba(255, 255, 255, 0.05)'
                          }}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              gap: { xs: 1, sm: 2 },
                              mb: 1,
                              overflow: 'hidden'
                            }}>
                              <FavoriteIcon sx={{ 
                                fontSize: { xs: 18, sm: 20 },
                                color: '#ef4444',
                                flexShrink: 0
                              }} />
                              <Typography variant="h6" sx={{ 
                                fontSize: { xs: '1rem', sm: '1.1rem' }, 
                                lineHeight: 1.2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {(video.stats?.diggCount || 0).toLocaleString()}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              Likes
                            </Typography>
                          </Box>

                          {/* Views */}
                          <Box sx={{ 
                            flex: '1 1 45%',
                            display: 'flex',
                            flexDirection: 'column',
                            p: 2,
                            borderRadius: 2,
                            bgcolor: 'rgba(255, 255, 255, 0.05)'
                          }}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              gap: { xs: 1, sm: 2 },
                              mb: 1,
                              overflow: 'hidden'
                            }}>
                              <VisibilityIcon sx={{ 
                                fontSize: { xs: 18, sm: 20 },
                                background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                flexShrink: 0
                              }} />
                              <Typography variant="h6" sx={{ 
                                fontSize: { xs: '1rem', sm: '1.1rem' }, 
                                lineHeight: 1.2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {(video.stats?.playCount || 0).toLocaleString()}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              Views
                            </Typography>
                          </Box>

                          {/* Like Rate */}
                          <Box sx={{ 
                            flex: '1 1 45%',
                            display: 'flex',
                            flexDirection: 'column',
                            p: 2,
                            borderRadius: 2,
                            bgcolor: 'rgba(255, 255, 255, 0.05)'
                          }}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              gap: { xs: 1, sm: 2 },
                              mb: 1,
                              overflow: 'hidden'
                            }}>
                              <FavoriteIcon sx={{ 
                                fontSize: { xs: 18, sm: 20 },
                                background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                flexShrink: 0
                              }} />
                              <Typography variant="h6" sx={{ 
                                fontSize: { xs: '1rem', sm: '1.1rem' }, 
                                lineHeight: 1.2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {calculateLikeRate(video).toFixed(2)}%
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              Like Rate
                            </Typography>
                          </Box>

                          {/* CTR */}
                          <Box sx={{ 
                            flex: '1 1 45%',
                            display: 'flex',
                            flexDirection: 'column',
                            p: 2,
                            borderRadius: 2,
                            bgcolor: 'rgba(255, 255, 255, 0.05)'
                          }}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              gap: { xs: 1, sm: 2 },
                              mb: 1,
                              overflow: 'hidden'
                            }}>
                              <ShareIcon sx={{ 
                                fontSize: { xs: 18, sm: 20 },
                                background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                flexShrink: 0
                              }} />
                              <Typography variant="h6" sx={{ 
                                fontSize: { xs: '1rem', sm: '1.1rem' }, 
                                lineHeight: 1.2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {calculateCTR(video).toFixed(2)}%
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              CTR
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ 
                          p: 3, 
                          borderRadius: 2,
                          background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                          color: 'white',
                          mt: 'auto'
                        }}>
                          <Typography variant="subtitle2">
                            Performance: {calculateLikeRate(video) > ((overallStats.totalLikes / overallStats.totalViews) * 100) ? 'Above' : 'Below'} Average
                          </Typography>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </>
        )}
      </Box>
    </Container>
  );
};

export default LikeRateAnalysis;
